import React from 'react';
import { Target, TrendingUp, Users } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { WelcomeBanner, greetingFor } from '../../infrastructure/widgets/WelcomeBanner';

interface ForeignAmount { currency: string; count: number; value: number }

/** "$12,400 · 2 USD" — a foreign-currency amount in its own currency. */
function ForeignLines({ items, noun }: { items: ForeignAmount[]; noun: string }) {
  if (!items.length) return null;
  return (
    <>
      {items.map((f) => {
        let money: string;
        try { money = new Intl.NumberFormat('en-US', { style: 'currency', currency: f.currency, maximumFractionDigits: 0 }).format(f.value); }
        catch { money = `${f.currency} ${Math.round(f.value).toLocaleString('en-IN')}`; }
        return (
          <div key={f.currency} className="mt-1 block w-fit rounded-lg bg-surface-variant px-1.5 py-0.5 text-data-xs font-medium text-on-surface-variant">
            <span className="font-mono tabular-nums">{money}</span>
            <span className="text-on-surface-subtle"> · {f.count} {f.currency} {noun}{f.count === 1 ? '' : 's'}</span>
          </div>
        );
      })}
    </>
  );
}

interface SalesEmployeeHeroProps {
  displayName: string;
  pipelineValue?: number;
  openDealsCount?: number;
  wonMonthValue?: number;
  wonDealsCount?: number;
  /** Collected against this month's invoices; null = not known. */
  collectedMonth?: number | null;
  pipelineForeign?: ForeignAmount[];
  monthForeign?: ForeignAmount[];
  totalLeads?: number;
  leadsWeek?: number;
  overdueCount?: number;
  visitsCount?: number;
  /** 'section' under another page's banner (Team Dashboard → My work). */
  variant?: 'hero' | 'section';
}

export const SalesEmployeeHero: React.FC<SalesEmployeeHeroProps> = ({
  displayName,
  pipelineValue = 0,
  openDealsCount = 0,
  wonMonthValue = 0,
  wonDealsCount = 0,
  collectedMonth = null,
  pipelineForeign = [],
  monthForeign = [],
  totalLeads = 0,
  leadsWeek = 0,
  overdueCount = 0,
  visitsCount = 0,
  variant = 'hero',
}) => {
  // The shared WelcomeBanner (2026-10-23): greeting, today, the two numbers that
  // decide what happens next, and the three figures of the desk.
  const first = displayName.trim().split(/\s+/)[0] || 'there';
  return (
    <WelcomeBanner
      variant={variant}
      title={variant === 'section' ? 'My desk · today' : `${greetingFor()}, ${first}`}
      subtitle={(
        <>
          {overdueCount > 0 ? (
            <>
              <span className="font-mono font-semibold tabular-nums text-danger-text">{overdueCount}</span> follow-
              {overdueCount === 1 ? 'up is' : 'ups are'} overdue
            </>
          ) : (
            'No follow-up is overdue'
          )}
          {' · '}
          <span className="font-mono font-semibold tabular-nums text-on-surface">{visitsCount}</span> visit
          {visitsCount === 1 ? '' : 's'} scheduled today.
        </>
      )}
      stats={[
        {
          label: 'My pipeline', value: formatINR(pipelineValue), icon: Target, tone: 'primary',
          sub: <>{openDealsCount} open deal{openDealsCount === 1 ? '' : 's'}<ForeignLines items={pipelineForeign} noun="deal" /></>,
        },
        {
          label: 'Sales this month', value: formatINR(wonMonthValue), icon: TrendingUp, tone: 'success',
          sub: (
            <>
              {wonDealsCount} invoice{wonDealsCount === 1 ? '' : 's'}
              {collectedMonth != null ? ` · ${formatINR(collectedMonth)} collected` : ''}
              <ForeignLines items={monthForeign} noun="export invoice" />
            </>
          ),
        },
        { label: 'My leads', value: String(totalLeads), icon: Users, tone: 'info', sub: `${leadsWeek} this week` },
      ]}
    />
  );
};
